import { Edit, ExpandMore } from '@mui/icons-material';
import { Grid, IconButton } from '@mui/material';
import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import clsx from 'clsx';
import { Fragment, useEffect, useState } from 'react';
import { Button, Card, Loader, Page, PageTitle, Toast } from '../../../components';
import { getPlantLists } from '../../../fetch';
import { IPlantList } from '../../../models';
import { EditPlantListModal } from './EditPlantListModal';

export const ManagePlantLists = () => {
  const [plants, setPlants] = useState<IPlantList[]>([]);
  const [isError, showError] = useState<boolean>(false);
  const [isExpanded, setExpanded] = useState<{
    [name: string]: boolean;
  }>({
    'List A': true,
    'List B': true,
    'List C': true
  });
  const [isLoadingPlants, setLoadingPlantLists] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [isPlantListModalOpen, showPlantListModal] = useState<{
    [name: string]: boolean;
  }>({
    'List A': false,
    'List B': false,
    'List C': false
  });

  const classes = useStyles();

  const fetchPlantLists = async () => {
    try {
      setLoadingPlantLists(true);
      const res = await getPlantLists();
      setPlants(res);
    } catch (error) {
      setErrorMessage('Error loading plant lists, please refresh and try again');
      console.log(error);
    } finally {
      setLoadingPlantLists(false);
    }
  };

  useEffect(() => {
    fetchPlantLists();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const lists = [
    {
      name: 'List A',
      listPlants: plants.filter(plant => plant.list === 'A')
    },
    {
      name: 'List B',
      listPlants: plants.filter(plant => plant.list === 'B')
    },
    {
      name: 'List C',
      listPlants: plants.filter(plant => plant.list === 'C')
    }
  ];

  return (
    <Page title="Manage Plant Lists">
      <PageTitle title="Manage Plant Lists" />
      {isLoadingPlants && <Loader position="top-center" />}
      {!isLoadingPlants && plants && plants.length > 0 && (
        <Grid container spacing={2}>
          {lists.map((list, index) => {
            return (
              <Fragment key={`${index}`}>
                <Grid item xs={12} md={4}>
                  <Card
                    title={list.name}
                    isExpanded={isExpanded[list.name]}
                    icon={
                      <IconButton
                        className={clsx(classes.expand, {
                          [classes.expandOpen]: isExpanded[list.name]
                        })}
                        onClick={() =>
                          setExpanded({ ...isExpanded, [list.name]: !isExpanded[list.name] })
                        }
                        aria-expanded={isExpanded[list.name]}
                        aria-label="show more"
                        size="large"
                      >
                        <ExpandMore />
                      </IconButton>
                    }
                    action={
                      <Button
                        className={classes.editButton}
                        id="edit"
                        startIcon={<Edit />}
                        onClick={e => {
                          e.stopPropagation();
                          showPlantListModal({ [list.name]: true });
                        }}
                      >
                        Edit
                      </Button>
                    }
                  >
                    <ul className={classes.listWrapper}>
                      {list.listPlants
                        .sort((a, b) => a.order - b.order)
                        .map((plant, plantIndex) => {
                          return <li key={`${plantIndex}`}>{plant.name}</li>;
                        })}
                    </ul>
                  </Card>
                </Grid>
              </Fragment>
            );
          })}
        </Grid>
      )}

      <Toast
        id="plant-list-error"
        autoHideDuration={5000}
        message={errorMessage}
        open={isError}
        onClose={() => showError(false)}
        variant="error"
      />
      {plants &&
        plants.length > 0 &&
        lists.map((list, index) => {
          return (
            <Fragment key={`${index}`}>
              <EditPlantListModal
                listName={list.name}
                open={isPlantListModalOpen[list.name] || false}
                plantsFullCollection={plants}
                plants={list.listPlants}
                fetchPlantLists={fetchPlantLists}
                onClose={() => {
                  showPlantListModal({ [list.name]: false });
                }}
              />
            </Fragment>
          );
        })}
    </Page>
  );
};

const useStyles = makeStyles((theme: Theme) => ({
  paginationWrapper: {
    margin: theme.spacing(0.5, 0)
  },
  expand: {
    color: '#ffffff',
    transform: 'rotate(0deg)',
    padding: '12px 0',
    marginLeft: 'auto',
    transition: theme.transitions.create('transform', {
      duration: theme.transitions.duration.shortest
    })
  },
  expandOpen: {
    transform: 'rotate(180deg)'
  },
  editButton: {
    color: '#fff',
    padding: 0,
    paddingRight: 12
  },
  listWrapper: {
    paddingLeft: 16
  }
}));
